import { useMemo, useState, useCallback, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  generatePickupTimeSlots,
  getDefaultPickupDateRange,
  isNowPickupSlot,
} from '@hero/core';

export interface TimeSlot {
  value: string;
  label: string;
  fromTime: string;
  toTime: string;
  from: Dayjs;
  to: Dayjs;
  isNow: boolean;
}

interface DateRange {
  from: Date;
  to: Date;
}

export interface UseOrderTimeSlotsOptions {
  disabledAutSelectDate?: boolean;
  dateRange?: DateRange | null;
}

export interface UseOrderTimeSlotsReturn {
  availableSlots: TimeSlot[];
  selectedSlot: string | null;
  selectedDate: Dayjs;
  dateRange: DateRange | undefined;
  selectSlot: (slotValue: string | null) => void;
  selectDate: (date: Dayjs | string | Date) => void;
  getDefaultSlot: () => string | null;
  isDateValid: (date: Dayjs | string | Date) => boolean;
}

const toDayjsSlots = (slots: ReturnType<typeof generatePickupTimeSlots>): TimeSlot[] =>
  slots.map(s => ({
    value: s.value,
    label: s.label,
    fromTime: s.fromTime,
    toTime: s.toTime,
    from: dayjs(s.from),
    to: dayjs(s.to),
    isNow: s.isNow,
  }));

/**
 * @deprecated Prefer `getDefaultPickupDateRange` from `@hero/core`.
 * Kept for existing imports from `@hero/hooks-shared`.
 */
export function getAutoDateRange(): DateRange {
  return getDefaultPickupDateRange();
}

export function isNowSlot(from: Date | string | Dayjs, now: Dayjs = dayjs()): boolean {
  return isNowPickupSlot(from instanceof Date || typeof from === 'string' ? from : from.toDate(), {
    now: now.toDate(),
  });
}

const generateTimeSlots = (selectedDate: Dayjs, currentTime: Dayjs): TimeSlot[] =>
  toDayjsSlots(
    generatePickupTimeSlots(selectedDate.toDate(), { now: currentTime.toDate() }),
  );

const getDefaultSlotValue = (selectedDate: Dayjs, availableSlots: TimeSlot[]): string | null => {
  if (availableSlots.length === 0) return null;
  const recommendedSlot = availableSlots.find(slot => slot.from.hour() === 9);
  if (recommendedSlot) return recommendedSlot.value;
  return availableSlots[0]?.value ?? null;
};

export function useOrderTimeSlots(options: UseOrderTimeSlotsOptions = {}): UseOrderTimeSlotsReturn {
  const { dateRange: dateRangeProp, disabledAutSelectDate = false } = options;

  const [selectedDate, setSelectedDate] = useState<Dayjs>(() => {
    if (dateRangeProp?.from) return dayjs(dateRangeProp.from);
    if (!disabledAutSelectDate) return dayjs(getAutoDateRange().from);
    return dayjs().startOf('day');
  });

  const dateRangeFromTimestamp = dateRangeProp?.from?.getTime();

  useEffect(() => {
    if (dateRangeProp?.from) {
      const newDate = dayjs(dateRangeProp.from);
      if (!newDate.isSame(selectedDate, 'day')) setSelectedDate(newDate);
    } else if (!dateRangeProp && !disabledAutSelectDate) {
      const defaultDate = dayjs(getAutoDateRange().from);
      if (!defaultDate.isSame(selectedDate, 'day')) setSelectedDate(defaultDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeFromTimestamp, disabledAutSelectDate]);

  const currentTime = useMemo(() => dayjs(), []);

  const availableSlots = useMemo(
    () => generateTimeSlots(selectedDate, currentTime),
    [selectedDate, currentTime],
  );

  const defaultSlot = useMemo(
    () => getDefaultSlotValue(selectedDate, availableSlots),
    [selectedDate, availableSlots],
  );

  const [selectedSlot, setSelectedSlot] = useState<string | null>(() => defaultSlot);

  useEffect(() => {
    if (defaultSlot) setSelectedSlot(defaultSlot);
  }, [defaultSlot]);

  const selectSlot = useCallback(
    (slotValue: string | null) => {
      setSelectedSlot(slotValue);
      if (slotValue && availableSlots.length > 0) {
        const slot = availableSlots.find(s => s.value === slotValue);
        if (slot) {
          const slotDate = slot.from.startOf('day');
          if (!slotDate.isSame(selectedDate, 'day')) setSelectedDate(slotDate);
        }
      }
    },
    [availableSlots, selectedDate],
  );

  const selectDate = useCallback((date: Dayjs | string | Date) => {
    setSelectedDate(dayjs(date));
  }, []);

  const getDefaultSlot = useCallback(() => defaultSlot, [defaultSlot]);

  const isDateValid = useCallback((date: Dayjs | string | Date) => {
    const dateToCheck = dayjs(date);
    const today = dayjs().startOf('day');
    return dateToCheck.isSame(today, 'day') || dateToCheck.isAfter(today, 'day');
  }, []);

  const dateRange = useMemo<DateRange | undefined>(() => {
    if (selectedSlot && availableSlots.length > 0) {
      const slot = availableSlots.find(s => s.value === selectedSlot);
      if (slot) return { from: slot.from.toDate(), to: slot.to.toDate() };
    }
    return undefined;
  }, [selectedSlot, availableSlots]);

  return {
    availableSlots,
    selectedSlot,
    selectedDate,
    dateRange,
    selectSlot,
    selectDate,
    getDefaultSlot,
    isDateValid,
  };
}
